import TextInputField from 'components/TextInputField';
import './JoinBetaTab.css';
import ActionButton from 'components/ActionButton';
import { Controller, useForm } from 'react-hook-form';
import { LOGIN_EMAIL_PLACEHOLDER } from '../../constants';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { AssetUrls } from 'config/assets';
import { useLazyCheckEmailExistsQuery } from 'store/services/auth/authApi';
import { useOnboardingStartMutation } from 'store/services/onboarding/onboardingApi';
import { submitJoinEmail } from './joinSubmit';

const JOIN_BETA_TITLE = 'Join TradeMagic';
const JOIN_BETA_DESCRIPTION = 'Start by entering your email address.';
const JOIN_BETA_BUTTON = 'Create Account';

export const loginValidationRules = {
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[\w+.-]+@([\w-]+\.)+[\w-]{2,4}$/,
      message: 'Please enter a valid email address',
    },
  },
};

interface JoinBetaTabProps {
  onSwitchToSignIn?: (email: string) => void;
  /** Email carried over from the Sign in tab's "Sign up" link. */
  initialEmail?: string;
}

const JoinBetaTab = ({ onSwitchToSignIn, initialEmail }: JoinBetaTabProps) => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: { email: initialEmail ?? '' } });

  const [onboardingStart] = useOnboardingStartMutation();
  // The existence check runs on blur and on submit, never while typing, so
  // normal typing cannot reach the backend's per-IP limit on this endpoint
  // (Asana 1219793876039315). Submit waits for the answer: an existing email
  // switches to the Sign in tab instead of reaching step 2.
  const [checkEmailExists] = useLazyCheckEmailExistsQuery();
  const [emailIsRegistered, setEmailIsRegistered] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const isWellFormed = (email: string) => loginValidationRules.email.pattern.value.test(email);

  const checkOnBlur = async () => {
    const email = getValues('email');
    if (!isWellFormed(email)) return;
    try {
      const { exists } = await checkEmailExists(email).unwrap();
      setEmailIsRegistered(exists);
    } catch {
      // A failed or rate-limited check leaves the prompt alone; submit decides.
    }
  };

  // Show toast notification when there's a validation error
  useEffect(() => {
    if (errors.email) {
      toast.error(errors.email.message || 'Please enter a valid email address');
    }
  }, [errors.email]);

  const onSubmit = async (data: { email: string }) => {
    if (isChecking) return;
    setIsChecking(true);
    try {
      await submitJoinEmail({
        email: data.email,
        checkEmailExists: (email) => checkEmailExists(email).unwrap(),
        onExists: (email) => {
          setEmailIsRegistered(true);
          // The Sign in tab gets the email through component state, never the URL.
          onSwitchToSignIn?.(email);
        },
        onNew: (email) => {
          // Funnel telemetry Step 1 (fire-and-forget, must never block signup).
          onboardingStart({ email }).unwrap().catch(() => {});
          // Navigate to onboarding with the email as a query parameter
          navigate(`/onboarding?email=${encodeURIComponent(email)}`);
        },
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleSwitchToSignIn = () => {
    onSwitchToSignIn?.(getValues('email'));
  };

  return (
    <div className="join-beta">
      <div className="join-beta-messages">
        <div className="join-beta-title">
          {JOIN_BETA_TITLE}
        </div>
        <div className="join-beta-description">
          {JOIN_BETA_DESCRIPTION}
        </div>
      </div>
      <div className="join-beta-input">
        <Controller
          name="email"
          control={control}
          rules={loginValidationRules.email}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_EMAIL_PLACEHOLDER}
              value={field.value}
              error={Boolean(errors.email)}
              onChange={(value) => {
                field.onChange(value);
                if (emailIsRegistered) setEmailIsRegistered(false);
              }}
              onBlur={() => void checkOnBlur()}
            />
          )}
        />
        {emailIsRegistered && (
          <p className="join-beta-email-exists-prompt">
            Looks like you already have an account.{' '}
            <button type="button" onClick={handleSwitchToSignIn}>
              Switch to Sign In?
            </button>
          </p>
        )}
        <ActionButton
          label={JOIN_BETA_BUTTON}
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="trademagic-icon"
            />
          }
          onPress={handleSubmit(onSubmit)}
          isLoading={isChecking}
        />
        <p className="join-beta-helper-link">
          Already have an account?{' '}
          <button type="button" onClick={handleSwitchToSignIn}>
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};

export default JoinBetaTab;
